import React, { ReactNode, useEffect } from 'react';
import { useQuery } from 'convex/react';
import { useLocation } from 'wouter';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';
import { FullPageLoading } from '@/components/FullPageLoading';
import { useContextRouting } from '@/hooks/useContextRouting';
import { Lock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

interface ContextRouteGuardProps {
  children: ReactNode;
  contextId?: string; // Optional - if not provided, uses currentContextId from routing
}

function AccessDeniedPage({ inline = false }: { inline?: boolean }) {
  const [, navigate] = useLocation();
  
  if (inline) {
    return (
      <div className="max-w-md mx-auto mt-16 p-6">
        <Alert variant="destructive">
          <Lock className="h-5 w-5" />
          <AlertTitle>Context Not Available</AlertTitle>
          <AlertDescription className="mt-2 space-y-3">
            <p>
              This context doesn't exist or you don't have permission to access it.
            </p>
            <p className="text-sm text-muted-foreground">
              If you believe you should have access, please contact the context owner.
            </p>
            <div className="flex gap-2 pt-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/')}
              >
                Go to Home
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.history.back()}
              >
                Go Back
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  return (
    <div className="flex items-center justify-center h-screen">
      <div className="text-center">
        <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
        <p className="text-gray-600">You don't have permission to access this context.</p>
      </div>
    </div>
  );
}

// Check if a string is a valid Convex ID format
function isValidConvexId(id: string): boolean {
  // Convex IDs are base64url strings with specific patterns
  // They should not contain special prefixes like "__"
  return !id.startsWith('__') && /^[a-zA-Z0-9_-]+$/.test(id);
}

export function ContextRouteGuard({ children, contextId: propContextId }: ContextRouteGuardProps) {
  const [, navigate] = useLocation();
  const { currentContextId } = useContextRouting();
  
  // Use prop contextId if provided, otherwise use currentContextId from routing
  const contextId = propContextId || currentContextId;
  const isInline = !propContextId; // If no contextId prop, we're being used inline
  
  // Always call hooks - even if we skip validation
  const hasAccess = useQuery(
    api.contexts.queries.checkAccess, 
    contextId && isValidConvexId(contextId) 
      ? { contextId: contextId as Id<'contexts'> } 
      : 'skip'
  );
  
  // Redirect if contextId is not a valid format
  useEffect(() => {
    if (contextId && !isValidConvexId(contextId)) {
      navigate('/contexts');
    }
  }, [contextId, navigate]);
  
  // Skip validation if no context ID
  if (!contextId) {
    return <>{children}</>;
  }
  
  // If invalid ID, show loading while redirect happens
  if (!isValidConvexId(contextId)) {
    return isInline ? <>{children}</> : <FullPageLoading />;
  }
  
  // Loading state
  if (hasAccess === undefined) {
    if (isInline) {
      return (
        <div className="flex items-center justify-center h-full">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      );
    }
    return <FullPageLoading />;
  }
  
  // No access to context
  if (!hasAccess) {
    return <AccessDeniedPage inline={isInline} />;
  }
  
  // Valid context - render children
  return <>{children}</>;
}